import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionPaymentClaimDetail } from './hooks/useConstructionPayment';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-500',
  submitted: 'bg-amber-100 text-amber-600',
  under_review: 'bg-blue-100 text-blue-600',
  approved: 'bg-emerald-500 text-white',
  rejected: 'bg-red-500 text-white',
  partially_paid: 'bg-indigo-100 text-indigo-600',
  paid: 'bg-indigo-500 text-white',
  cancelled: 'bg-slate-900 text-white',
};

export default function PaymentClaimDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const { claim: item, isLoading, updateStatus } = useConstructionPaymentClaimDetail(id);

  const [activeTab, setActiveTab] = useState('overview');

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết đề nghị thanh toán...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin đề nghị thanh toán</div>;

  const handleApprove = async () => {
    try {
      await updateStatus({ status: 'approved', userId: profile?.id || '' });
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'items', label: 'Dòng thanh toán' },
    { value: 'inspections', label: 'Nghiệm thu khối lượng' },
    { value: 'approvals', label: 'Phê duyệt' },
    { value: 'records', label: 'Lịch sử thanh toán' },
    { value: 'documents', label: 'Tài liệu đính kèm' },
    { value: 'logs', label: 'Nhật ký' },
  ];

  return (
    <PageDetailLayout
      title={item.claim_title}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {item.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono tracking-tighter">#{item.claim_code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-calendar-todo-line" />
            Kỳ thanh toán: <span className="font-bold text-os-primary">{item.claim_period_start ? new Date(item.claim_period_start).toLocaleDateString('vi-VN') : '---'} - {item.claim_period_end ? new Date(item.claim_period_end).toLocaleDateString('vi-VN') : '---'}</span>
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Thanh toán', path: '/construction/payments' },
        { label: 'Chi tiết' }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'submitted' && (
            <AuthButton module="construction" action="approve_payment" variant="success" icon="ri-check-line" onClick={handleApprove}>
              Duyệt thanh toán
            </AuthButton>
          )}
          {item.status_code === 'approved' && (
            <AuthButton module="construction" action="record_payment" variant="primary" icon="ri-money-dollar-box-line">
              Ghi nhận đã chi
            </AuthButton>
          )}
          <Button variant="outline" icon="ri-printer-line">In hồ sơ Claim</Button>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Giá trị Net" value={item.net_amount.toLocaleString()} icon="ri-bank-card-line" variant="primary" suffix=" VNĐ" />
                <KPICard title="Tiền giữ lại" value={item.retention_amount.toLocaleString()} icon="ri-safe-2-line" variant="warning" suffix=" VNĐ" />
                <KPICard title="Dự kiến VAT" value={item.vat_amount.toLocaleString()} icon="ri-percent-line" variant="info" suffix=" VNĐ" />
              </div>

              <Card className="p-0 overflow-hidden border-none shadow-sm">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2 text-sm">
                    <i className="ri-list-check text-os-primary" />
                    Chi tiết khối lượng thanh toán (Top 5)
                  </h3>
                  <Badge variant="neutral" className="text-[10px]">{item.items?.length || 0} Hạng mục</Badge>
                </div>
                <Table
                  columns={[
                    { key: 'code', title: 'Mã BOQ' },
                    { key: 'desc', title: 'Nội dung công việc' },
                    { key: 'qty', title: 'Khối lượng đợt này', align: 'right' },
                    { key: 'amount', title: 'Thành tiền', align: 'right' },
                  ]}
                  data={item.items?.slice(0, 5) || []}
                  renderCell={(row, key) => {
                    switch (key) {
                      case 'code':
                        return <span className="font-mono text-[10px] font-bold text-slate-400">{row.boq_item?.item_code}</span>;
                      case 'desc':
                        return <span className="text-xs font-bold text-slate-700 line-clamp-1">{row.description}</span>;
                      case 'qty':
                        return <span className="text-xs font-bold text-slate-900">{row.current_claim_quantity} {row.unit}</span>;
                      case 'amount':
                        return <span className="text-xs font-bold text-os-primary">{row.current_amount.toLocaleString()}</span>;
                      default: return null;
                    }
                  }}
                />
                {item.items && item.items.length > 5 && (
                  <Button variant="ghost" size="sm" className="w-full border-t border-slate-50 rounded-none text-[10px]" onClick={() => setActiveTab('items')}>
                    Xem tất cả {item.items.length} hạng mục thanh toán
                  </Button>
                )}
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-calculator-line text-os-primary" />
                  Bảng tổng hợp giá trị (Calculation)
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center pb-2 border-b border-slate-50">
                    <span className="text-sm text-slate-500">Giá trị hoàn thành (Gross)</span>
                    <span className="text-sm font-bold text-slate-900">{item.gross_amount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-slate-50">
                    <span className="text-sm text-slate-500">Khấu trừ tiền giữ lại (Retention)</span>
                    <span className="text-sm font-bold text-red-500">- {item.retention_amount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-slate-50">
                    <span className="text-sm text-slate-500">Khấu trừ tạm ứng (Advance Deduction)</span>
                    <span className="text-sm font-bold text-red-500">- {item.advance_deduction_amount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-slate-50">
                    <span className="text-sm text-slate-500">Thuế giá trị gia tăng (VAT)</span>
                    <span className="text-sm font-bold text-emerald-500">+ {item.vat_amount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-base font-bold text-slate-900">Giá trị thanh toán kỳ này (Net)</span>
                    <span className="text-xl font-black text-os-primary underline decoration-double">{item.net_amount.toLocaleString()} VNĐ</span>
                  </div>
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6 bg-slate-900 text-white">
                <h4 className="font-bold mb-4 flex items-center gap-2 text-os-primary">
                  <i className="ri-user-received-line" />
                  Thông tin hồ sơ thanh toán
                </h4>
                <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-2xl border border-slate-700">
                  <Avatar name={item.submitter?.full_name} src={item.submitter?.avatar_url} size="sm" />
                  <div className="flex flex-col">
                    <p className="text-[10px] text-slate-400 font-bold ">Người lập hồ sơ</p>
                    <p className="text-sm font-bold text-slate-200">{item.submitter?.full_name}</p>
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="p-3 bg-slate-800 rounded-2xl border border-slate-700">
                    <p className="text-[10px] text-slate-400 font-bold  mb-1">Dự án</p>
                    <p className="text-xs font-bold text-os-primary italic">{item.project?.name}</p>
                  </div>
                  <div className="p-3 bg-slate-800 rounded-2xl border border-slate-700">
                    <p className="text-[10px] text-slate-400 font-bold  mb-1">Loại đợt</p>
                    <Badge variant="primary" className="text-[10px] font-bold ">{item.claim_type}</Badge>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-shield-check-line text-slate-400" />
                  Luồng phê duyệt tài chính
                </h4>
                <div className="space-y-6 relative before:absolute before:left-2 before:top-2 before:bottom-2 before:w-px before:bg-slate-100">
                  <div className="relative pl-6 flex flex-col">
                    <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-emerald-500 border-4 border-white shadow-sm" />
                    <span className="text-[10px] font-bold text-slate-900 ">Hồ sơ đã gửi</span>
                    <span className="text-[9px] text-slate-400 italic">QS đã hoàn tất đối soát khối lượng</span>
                  </div>
                  <div className="relative pl-6 flex flex-col opacity-50">
                    <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-slate-200 border-4 border-white shadow-sm" />
                    <span className="text-[10px] font-bold text-slate-400 ">Phê duyệt PM</span>
                    <span className="text-[9px] text-slate-400">Đang chờ PM ký duyệt</span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {(activeTab === 'items' || activeTab === 'inspections' || activeTab === 'approvals' || activeTab === 'records' || activeTab === 'documents' || activeTab === 'logs') && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu đang được đồng bộ từ bảng tổng hợp khối lượng nghiệm thu, nhật ký phê duyệt đa cấp và chứng từ kế toán thực tế."
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
